import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import { Control, UseFormRegister, useForm } from 'react-hook-form';
import { ProjectDeepFragment } from '../../../gql/graphql';
import {
  EventName,
  EventProperties,
  useMixpanel,
} from '../../../services/mixpanel';
import MultilabelInput from '../../orderContractor/MultilabelInput';

type NewOrderModalProps = {
  project: ProjectDeepFragment;
  orderModalIsOpen: boolean;
  closeOrderModal: () => void;
  onSubmitNewOrder: (
    title: string,
    labels: string
  ) => Promise<string | undefined>;
};

type NewOrderFormData = {
  title: string;
  labels: { key: string; value: string }[];
};

export default function NewOrderModal({
  project,
  orderModalIsOpen,
  closeOrderModal,
  onSubmitNewOrder,
}: NewOrderModalProps): React.ReactElement {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    control,
  } = useForm<NewOrderFormData>({ defaultValues: { labels: [] } });

  const mixpanelProperties: EventProperties = useMemo(
    () => ({
      'Project Name': project.name,
      'Project ID': project.id,
    }),
    [project.id, project.name]
  );

  const { trackEvent } = useMixpanel();

  useEffect(() => {
    reset({
      labels:
        project.settings?.allowedOrderLabels?.map((labelName) => ({
          key: labelName.name,
          value: '',
        })) ?? [],
    });
  }, [project.settings, reset]);

  const onSubmit = handleSubmit(async ({ title, labels }) => {
    const nonEmptyLabels = labels.filter(
      (label) => label.key.length > 0 && label.value.length > 0
    );

    const labelJson = JSON.stringify(nonEmptyLabels);

    await onSubmitNewOrder(title, labelJson);
    closeOrderModal();
    reset();

    trackEvent(EventName.TabledCreated, {
      ...mixpanelProperties,
      'Table Name': title,
      Labels: labels,
    });
  });

  return (
    <Modal isOpen={orderModalIsOpen} onClose={closeOrderModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Table</ModalHeader>
        <ModalCloseButton />
        <Divider orientation="horizontal" />
        <ModalBody>
          <form id="new-order-form" onSubmit={onSubmit}>
            <FormControl isRequired>
              <FormLabel>Table Name</FormLabel>
              <Input
                {...register('title')}
                autoFocus
                placeholder="Table name"
              />
            </FormControl>
            <MultilabelInput
              // jonasws: This was the only way to please the TypesScript gods, it seems
              control={
                control as unknown as Control<Pick<NewOrderFormData, 'labels'>>
              }
              register={
                register as unknown as UseFormRegister<
                  Pick<NewOrderFormData, 'labels'>
                >
              }
            />
          </form>
        </ModalBody>
        <Divider orientation="horizontal" mt={1} />
        <ModalFooter gap={2}>
          <Button
            isLoading={isSubmitting}
            colorScheme="brand"
            type="submit"
            form="new-order-form"
          >
            Create
          </Button>
          <Button colorScheme="gray" onClick={closeOrderModal}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
